import { Container, Graphics, Text, type Renderer } from 'pixi.js';

import {
  project,
  type County,
  type JourneyPoint,
  type Projection,
  type Router,
} from '@hotlist/shared';

import type { Viewport } from './viewport.ts';

/**
 * A plate's journey, traced on the map.
 *
 * Every camera that read the plate, plotted at the junction it guards. A read
 * count of sixty across a handful of poles is not sixty places -- it is a few
 * places, seen over and over -- so the poles are drawn as dots sized by how
 * often the plate passed them, and the order it passed them in is a faint line
 * underneath. What stands out is the shape of a life: the junction the car keeps
 * coming back to, brightest and biggest, and the last place the network saw it,
 * ringed. That is what an ALPR search is really for, and it is exactly why the
 * network is what it is -- no camera knows where the car is now, but the record
 * of where it has *been* is a picture somebody can read.
 *
 * An operator overlay, above the day/night wash so it reads at any hour.
 */
export class JourneyLayer {
  readonly container = new Container();
  private readonly path = new Graphics();
  private readonly dots = new Graphics();
  private readonly labels = new Container();
  /** Every read in time order, for the faint route line. */
  private route: { x: number; y: number }[] = [];
  /** Distinct cameras, with how many times the plate was read there. */
  private stops: { x: number; y: number; count: number }[] = [];
  private startAt = 0;
  private endAt = 0;
  private endLoc: { x: number; y: number } | null = null;
  private readonly projection: Projection;
  private readonly county: County;
  private daySeconds: number;

  constructor(_renderer: Renderer, projection: Projection, county: County, daySeconds: number) {
    this.projection = projection;
    this.county = county;
    this.daySeconds = daySeconds;
    this.container.addChild(this.path, this.dots, this.labels);
    this.container.eventMode = 'none';
    this.container.visible = false;
  }

  /** The county's day length, so the endpoint clocks read true. */
  setDaySeconds(seconds: number): void {
    if (Number.isFinite(seconds) && seconds > 0) this.daySeconds = seconds;
  }

  /** True while a journey is being shown. */
  get active(): boolean {
    return this.route.length > 0;
  }

  /**
   * Show the reads for a plate, oldest first so the line runs with travel. With
   * a router, each leg between two reads is drawn along the roads it would have
   * been driven, not bee-lined across country -- the county's roads are straight
   * junction-to-junction segments, so a routed edge list *is* the road path.
   */
  setJourney(points: readonly JourneyPoint[], router?: Router): void {
    const valid = [...points]
      .filter((p) => p.n >= 0 && p.n < this.county.nodeCount)
      .sort((a, b) => a.at - b.at);
    // The road-following line. Routing every leg is only worth it up to a point;
    // past a few hundred reads the A* cost is not worth a line nobody can read,
    // so it falls back to junction-to-junction there.
    const routeLegs = router !== undefined && valid.length <= 200;
    const poly: { x: number; y: number }[] = [];
    const pushNode = (n: number): void => {
      poly.push({ x: this.county.nodeX[n] as number, y: this.county.nodeY[n] as number });
    };
    if (valid.length) pushNode(valid[0]!.n);
    for (let i = 1; i < valid.length; i++) {
      const from = valid[i - 1]!.n;
      const to = valid[i]!.n;
      if (from === to) continue; // same pole twice: no leg to draw
      const edges = routeLegs ? router!.route(from, to) : null;
      if (edges && edges.length > 0) {
        let node = from;
        for (const e of edges) {
          const a = this.county.edgeA[e] as number;
          const b = this.county.edgeB[e] as number;
          node = a === node ? b : a;
          pushNode(node);
        }
      } else {
        pushNode(to); // no path found, or routing off: straight segment
      }
    }
    this.route = poly;
    // Collapse repeat reads at one pole into a single dot that counts them.
    const agg = new Map<number, { x: number; y: number; count: number }>();
    for (const p of valid) {
      const seen = agg.get(p.n);
      if (seen) seen.count++;
      else
        agg.set(p.n, {
          x: this.county.nodeX[p.n] as number,
          y: this.county.nodeY[p.n] as number,
          count: 1,
        });
    }
    this.stops = [...agg.values()];
    this.startAt = valid.length ? (valid[0]!.at as number) : 0;
    this.endAt = valid.length ? (valid[valid.length - 1]!.at as number) : 0;
    const lastN = valid.length ? (valid[valid.length - 1]!.n as number) : -1;
    this.endLoc =
      lastN >= 0
        ? { x: this.county.nodeX[lastN] as number, y: this.county.nodeY[lastN] as number }
        : null;
    this.container.visible = this.route.length > 0;
  }

  clear(): void {
    this.route = [];
    this.stops = [];
    this.endLoc = null;
    this.container.visible = false;
    this.path.clear();
    this.dots.clear();
    for (const child of this.labels.removeChildren()) child.destroy();
  }

  update(viewport: Viewport): void {
    if (!this.container.visible || this.route.length === 0) return;
    const toScreen = (x: number, y: number): { sx: number; sy: number } => {
      const pr = project(this.projection, x, y);
      return { sx: viewport.toScreenX(pr.sx), sy: viewport.toScreenY(pr.sy) };
    };

    // The order it was seen in, underneath and quiet: a hint at the route, not a
    // web of bright lines fighting the dots.
    this.path.clear();
    if (this.route.length > 1) {
      const first = toScreen(this.route[0]!.x, this.route[0]!.y);
      this.path.moveTo(first.sx, first.sy);
      for (let i = 1; i < this.route.length; i++) {
        const s = toScreen(this.route[i]!.x, this.route[i]!.y);
        this.path.lineTo(s.sx, s.sy);
      }
      this.path.stroke({ width: 2.5, color: 0x3ad4ec, alpha: 0.7 });
    }

    // The poles, sized by how often -- the shape of the routine.
    this.dots.clear();
    let maxCount = 1;
    for (const s of this.stops) maxCount = Math.max(maxCount, s.count);
    for (const s of this.stops) {
      const p = toScreen(s.x, s.y);
      const r = 3 + 5 * Math.sqrt(s.count / maxCount);
      this.dots.circle(p.sx, p.sy, r + 3).fill({ color: 0x3ad4ec, alpha: 0.1 });
      this.dots.circle(p.sx, p.sy, r).fill({ color: 0x3ad4ec, alpha: 0.85 });
    }
    // The last place the network saw it, ringed.
    if (this.endLoc) {
      const e = toScreen(this.endLoc.x, this.endLoc.y);
      this.dots.circle(e.sx, e.sy, 11).stroke({ width: 2, color: 0xaef0ff, alpha: 0.95 });
    }

    // Clock the span, so the journey reads without a list beside it.
    for (const child of this.labels.removeChildren()) child.destroy();
    const start = toScreen(this.route[0]!.x, this.route[0]!.y);
    this.labels.addChild(this.stamp(clock(this.startAt, this.daySeconds), start.sx, start.sy, 0x9aa8bc));
    if (this.endLoc && this.route.length > 1) {
      const e = toScreen(this.endLoc.x, this.endLoc.y);
      this.labels.addChild(this.stamp(clock(this.endAt, this.daySeconds), e.sx, e.sy, 0xaef0ff));
    }
  }

  private stamp(text: string, x: number, y: number, fill: number): Text {
    const label = new Text({
      text,
      style: { fontFamily: 'ui-monospace, monospace', fontSize: 11, fill },
    });
    label.anchor.set(0.5, 1);
    label.position.set(x, y - 12);
    return label;
  }
}

/** hh:mm of the day, from sim seconds. */
function clock(seconds: number, daySeconds: number): string {
  const frac = (((seconds % daySeconds) + daySeconds) % daySeconds) / daySeconds;
  const minutes = Math.floor(frac * 24 * 60);
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
